import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Badge, Spinner, Button, Table, Accordion, Offcanvas } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { FaSearch, FaChevronRight, FaInfoCircle, FaDownload, FaUniversity, FaGraduationCap, FaFilter } from "react-icons/fa";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import Header from "../components/Header";
import Footer from "../components/Footer";
import API_BASE_URL from "../config";
import logo from "../assets/logo.png";

const FeeStructure = () => {
  const [feeData, setFeeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProg, setSelectedProg] = useState({ name: "", details: null, category: "" });
  const [lastSync, setLastSync] = useState("");
  const [showDrawer, setShowDrawer] = useState(false); // Mobile Drawer State

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/fetch/feeStructureData`);
      if (response.data?.[0]) {
        setFeeData(response.data[0]);
        setLastSync(new Date().toLocaleTimeString());
      }
    } catch (error) {
      console.error("Sync Error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSelect = (name, details, category) => {
    setSelectedProg({ name, details, category });
    setShowDrawer(false); // Close drawer on selection
  };

  // Logic: Remark filtering for Undergraduate
  const getFilteredRemarks = (remarks, programName, category) => {
    if (category !== "Undergraduate" || !Array.isArray(remarks)) return remarks;
    const matching = remarks.filter(r => r.toLowerCase().includes(programName.toLowerCase()));
    return matching.length > 0 ? matching : remarks.filter(r => !r.toLowerCase().includes("pay on their own"));
  };

  // PDF Export Replicating Schedule Page Quality
  const downloadPDF = () => {
    if (!selectedProg.details) return;
    const doc = new jsPDF();
    
    try { doc.addImage(logo, 'PNG', 14, 10, 25, 25); } catch (e) {}
    
    doc.setFont("helvetica", "bold").setFontSize(18).setTextColor(0, 43, 90);
    doc.text("SUKKUR IBA UNIVERSITY", 45, 20);
    doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(100);
    doc.text("Official Fee Structure Breakdown", 45, 27);
    
    doc.setDrawColor(0, 85, 164).setLineWidth(0.5).line(14, 40, 196, 40);

    doc.setFontSize(14).setTextColor(0, 43, 90).text(`${selectedProg.name}`, 14, 50);
    doc.setFontSize(10).setTextColor(100).text(`Category: ${selectedProg.category}`, 14, 56);

    const tableData = Object.entries(selectedProg.details)
      .filter(([key]) => key !== "_id" && key !== "financialYear")
      .map(([key, value]) => {
        const label = key.replace(/([A-Z])/g, " $1").toUpperCase();
        let displayValue = Array.isArray(value) ? value.join(", ") : value;
        if (key.toLowerCase() === "remarks") {
            displayValue = getFilteredRemarks(value, selectedProg.name, selectedProg.category).join(", ");
        }
        return [label, displayValue];
      });

    autoTable(doc, {
      startY: 65,
      head: [['PARTICULAR', 'DETAILS / AMOUNT (PKR)']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [0, 43, 90], fontSize: 11 },
      styles: { cellPadding: 5, fontSize: 10 },
    });

    doc.save(`${selectedProg.name}_Fee_Structure.pdf`);
  };

  if (loading) return (
    <div className="premium-loader">
      <Spinner animation="border" variant="primary" />
      <p className="mt-3 fw-bold letter-spacing-2">SYNCING FINANCIAL RECORDS</p>
    </div>
  );

  return (
    <div className="portal-master-wrapper">
      <Header />

      {/* Replicated Sub-Nav Styling */}
      <section className="portal-sub-nav py-4 shadow-sm bg-white sticky-top" style={{ top: '0', zIndex: 1020 }}>
        <Container>
          <Row className="align-items-center">
            <Col md={5}>
              <h1 className="h4 fw-900 text-navy m-0">Fee <span className="text-primary">Structure</span></h1>
              <small className="text-muted d-flex align-items-center gap-2">
                 <span className="sync-dot-mini"></span> Sync: {lastSync}
              </small>
            </Col>
            <Col md={7}>
              <div className="d-flex gap-3 justify-content-end align-items-center mt-3 mt-md-0">
                <div className="search-pill-container flex-grow-1">
                  <FaSearch className="search-icon" />
                  <input type="text" placeholder="Filter programs..." onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
                {/* Mobile Filter Toggle */}
                <Button 
                    variant="primary" 
                    className="rounded-pill px-4 d-lg-none d-flex align-items-center gap-2 nav-hint-pulse shadow-sm h-100"
                    onClick={() => setShowDrawer(true)}
                >
                    <FaFilter /> <span className="fw-bold small">Select Program</span>
                </Button>
                {selectedProg.details && (
                  <Button variant="outline-primary" className="rounded-pill px-4 d-none d-lg-flex align-items-center h-100" onClick={downloadPDF}>
                    <FaDownload className="me-2" /> PDF
                  </Button>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="portal-viewport bg-light-soft py-5">
        <Container className="main-portal-container section-standard-spacing position-relative" style={{ zIndex: 10 }}>
          <Row className="g-4">
            {/* Desktop Sidebar */}
            <Col lg={5} className="d-none d-lg-block">
              <div className="sticky-sidebar">
                <div className="sidebar-card border-0 shadow-sm">
                  <div className="card-label">AVAILABLE TRACKS</div>
                  <div className="program-list custom-scrollbar">
                  <Accordion defaultActiveKey="0" flush className="premium-accordion">
                    {/* Undergraduate */}
                    {feeData?.undergraduatePrograms?.[0] && (
                      <Accordion.Item eventKey="0">
                        <Accordion.Header><FaUniversity className="me-2" /> Undergraduate</Accordion.Header>
                        <Accordion.Body className="p-0">
                          {feeData.undergraduatePrograms[0].programNames
                            .filter(name => name.toLowerCase().includes(searchTerm.toLowerCase()))
                            .map((name, i) => (
                            <div key={i} className={`prog-item w-100 d-flex align-items-center justify-content-between p-3 border-0 bg-transparent transition-all ${selectedProg.name === name ? 'is-active' : ''}`}
                              onClick={() => handleSelect(name, feeData.undergraduatePrograms[0].financialYear, "Undergraduate")}>
                                <div className="d-flex align-items-center gap-3">
                                  <div className={`icon-circle-mini ${selectedProg.name === name ? 'active' : ''}`}>
                                    <FaUniversity />
                                  </div>
                                  <div className="text-start">
                                      <div className="name fw-bold mb-0">{name}</div>
                                      <div className="stats-tag">Undergraduate</div>
                                  </div>
                                </div>
                                <FaChevronRight className={`chevron-mini ${selectedProg.name === name ? 'visible' : ''}`} />
                            </div>
                          ))}
                        </Accordion.Body>
                      </Accordion.Item>
                    )}
                    {/* Graduate */}
                    {feeData?.graduatePrograms && (() => {
                      const allGrad = feeData.graduatePrograms.flatMap(g => 
                        g.programNames.map(name => ({ name, details: g.financialYear }))
                      );
                      
                      const searchLower = searchTerm.toLowerCase();
                      const masters = allGrad.filter(p => !p.name.toLowerCase().includes('m.phil') && !p.name.toLowerCase().includes('ph.d') && !p.name.toLowerCase().includes('phd') && p.name.toLowerCase().includes(searchLower));
                      const mphils = allGrad.filter(p => (p.name.toLowerCase().includes('m.phil') || p.name.toLowerCase().includes('mphil')) && p.name.toLowerCase().includes(searchLower));
                      const phds = allGrad.filter(p => (p.name.toLowerCase().includes('ph.d') || p.name.toLowerCase().includes('phd')) && p.name.toLowerCase().includes(searchLower));

                      return (
                        <Accordion.Item eventKey="1">
                          <Accordion.Header><FaGraduationCap className="me-2" /> Graduate Programs</Accordion.Header>
                          <Accordion.Body className="p-0">
                            {/* Masters Group */}
                            {masters.length > 0 && (
                              <>
                                <div className="px-4 py-2 bg-light small fw-bold text-primary">Masters Programs</div>
                                {masters.map((p, i) => (
                                  <div key={`ms-${i}`} className={`prog-item w-100 d-flex align-items-center justify-content-between p-3 border-0 bg-transparent transition-all ${selectedProg.name === p.name ? 'is-active' : ''}`}
                                    onClick={() => handleSelect(p.name, p.details, "Graduate")}>
                                      <div className="d-flex align-items-center gap-3">
                                        <div className={`icon-circle-mini ${selectedProg.name === p.name ? 'active' : ''}`}>
                                          <FaGraduationCap />
                                        </div>
                                        <div className="text-start">
                                            <div className="name fw-bold mb-0">{p.name}</div>
                                            <div className="stats-tag">Masters</div>
                                        </div>
                                      </div>
                                      <FaChevronRight className={`chevron-mini ${selectedProg.name === p.name ? 'visible' : ''}`} />
                                  </div>
                                ))}
                              </>
                            )}

                            {/* M.Phil Group */}
                            {mphils.length > 0 && (
                              <>
                                <div className="px-4 py-2 bg-light small fw-bold text-primary">M.Phil Programs</div>
                                {mphils.map((p, i) => (
                                  <div key={`mphil-${i}`} className={`prog-item w-100 d-flex align-items-center justify-content-between p-3 border-0 bg-transparent transition-all ${selectedProg.name === p.name ? 'is-active' : ''}`}
                                    onClick={() => handleSelect(p.name, p.details, "Graduate")}>
                                      <div className="d-flex align-items-center gap-3">
                                        <div className={`icon-circle-mini ${selectedProg.name === p.name ? 'active' : ''}`}>
                                          <FaGraduationCap />
                                        </div>
                                        <div className="text-start">
                                            <div className="name fw-bold mb-0">{p.name}</div>
                                            <div className="stats-tag">M.Phil</div>
                                        </div>
                                      </div>
                                      <FaChevronRight className={`chevron-mini ${selectedProg.name === p.name ? 'visible' : ''}`} />
                                  </div>
                                ))}
                              </>
                            )}

                            {/* PhD Group */}
                            {phds.length > 0 && (
                              <>
                                <div className="px-4 py-2 bg-light small fw-bold text-primary">PhD Programs</div>
                                {phds.map((p, i) => (
                                  <div key={`phd-${i}`} className={`prog-item w-100 d-flex align-items-center justify-content-between p-3 border-0 bg-transparent transition-all ${selectedProg.name === p.name ? 'is-active' : ''}`}
                                    onClick={() => handleSelect(p.name, p.details, "Graduate")}>
                                      <div className="d-flex align-items-center gap-3">
                                        <div className={`icon-circle-mini ${selectedProg.name === p.name ? 'active' : ''}`}>
                                          <FaGraduationCap />
                                        </div>
                                        <div className="text-start">
                                            <div className="name fw-bold mb-0">{p.name}</div>
                                            <div className="stats-tag">Doctorate</div>
                                        </div>
                                      </div>
                                      <FaChevronRight className={`chevron-mini ${selectedProg.name === p.name ? 'visible' : ''}`} />
                                  </div>
                                ))}
                              </>
                            )}
                          </Accordion.Body>
                        </Accordion.Item>
                      );
                    })()}
                  </Accordion>
                </div>
              </div>
            </div>
          </Col>

          {/* Main Content - Replicated Motion & Table Styling */}
           <Col lg={7}>
            <AnimatePresence mode="wait">
              {selectedProg.details ? (
                <motion.div key={selectedProg.name} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} className="details-card bg-white p-4 p-lg-5 rounded-4 shadow-sm">
                   <div className="section-intro mb-5">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="category-tag">Fee Breakdown</div>
                        <Button variant="link" className="text-primary d-lg-none" onClick={downloadPDF}><FaDownload /> PDF</Button>
                      </div>
                      <h2 className="portal-heading text-navy display-5 mb-3">{selectedProg.name}</h2>
                      <div className="ivy-divider mb-4" />
                      <div className="d-flex align-items-center gap-3">
                         <Badge bg="soft-primary" className="text-primary px-3 py-2 rounded-pill fw-bold">Academic Year {selectedProg.details.financialYear || '2024-25'}</Badge>
                         <p className="small text-muted mb-0">Official finance record</p>
                      </div>
                    </div>

                    <div className="table-responsive-wrapper">
                      <Table hover className="premium-fee-table mb-0">
                        <thead className="d-none d-lg-table-header-group">
                          <tr className="text-uppercase small letter-spacing-1">
                            <th>Description</th>
                            <th className="text-end">Value/Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(selectedProg.details).map(([key, value]) => {
                            if (key === "_id" || key === "financialYear") return null;
                            const isRemarks = key.toLowerCase() === "remarks";
                            const displayValue = isRemarks 
                              ? getFilteredRemarks(value, selectedProg.name, selectedProg.category).join(", ") 
                              : Array.isArray(value) ? value.join(", ") : value;

                            return (
                              <tr key={key} className="fee-row">
                                <td className="text-muted fw-500 py-lg-3 fee-label">
                                  {key.replace(/([A-Z])/g, " $1").toUpperCase()}
                                </td>
                                <td className={`text-lg-end fw-bold py-lg-3 fee-value ${isRemarks ? 'text-navy small italic remarks-cell' : 'text-primary'}`}>
                                  {displayValue}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                </motion.div>
              ) : (
                <div className="text-center py-5 bg-white rounded-4 shadow-sm details-card h-100 d-flex flex-column align-items-center justify-content-center border-dashed">
                  <FaInfoCircle size={50} className="text-muted mb-3 opacity-20" />
                  <h4 className="text-muted fw-bold">Select Program</h4>
                  <p className="text-muted small px-4">Choose a track from the sidebar to view the official fee breakdown for Academic Year 2024-25.</p>
                </div>
              )}
            </AnimatePresence>
            </Col>
          </Row>
        </Container>
      </section>
      
      {/* --- MOBILE PROGRAM DRAWER --- */}
      <Offcanvas show={showDrawer} onHide={() => setShowDrawer(false)} placement="bottom" className="h-75 rounded-top-4">
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title className="fw-bold text-navy">Select Fee Program</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
          <Accordion defaultActiveKey="0" flush className="premium-accordion">
            {/* Undergraduate */}
            {feeData?.undergraduatePrograms?.[0] && (
              <Accordion.Item eventKey="0">
                <Accordion.Header><FaUniversity className="me-2" /> Undergraduate</Accordion.Header>
                <Accordion.Body className="p-0">
                  {feeData.undergraduatePrograms[0].programNames
                    .filter(name => name.toLowerCase().includes(searchTerm.toLowerCase()))
                    .map((name, i) => (
                    <div key={i} className={`prog-item mobile-item w-100 d-flex align-items-center justify-content-between p-3 border-0 bg-transparent transition-all ${selectedProg.name === name ? 'is-active' : ''}`}
                      onClick={() => handleSelect(name, feeData.undergraduatePrograms[0].financialYear, "Undergraduate")}>
                        <div className="d-flex align-items-center gap-3">
                            <div className={`icon-circle-mini ${selectedProg.name === name ? 'active' : ''}`}>
                                <FaUniversity />
                            </div>
                            <div className="text-start">
                                <div className="name fw-bold mb-0">{name}</div>
                                <div className="stats-tag">Undergraduate</div>
                            </div>
                        </div>
                        {selectedProg.name === name && <FaChevronRight className="chevron-mini visible" />}
                    </div>
                  ))}
                </Accordion.Body>
              </Accordion.Item>
            )}
            {/* Graduate */}
            {feeData?.graduatePrograms && (
              <Accordion.Item eventKey="1">
                <Accordion.Header><FaGraduationCap className="me-2" /> Graduate Programs</Accordion.Header>
                <Accordion.Body className="p-0">
                  {feeData.graduatePrograms.map((group, gi) => (
                    <div key={gi}>
                      <div className="px-4 py-2 bg-light small fw-bold text-primary">{gi === 0 ? "Masters" : "M.Phil/PhD"}</div>
                      {group.programNames.filter(name => name.toLowerCase().includes(searchTerm.toLowerCase())).map((name, i) => (
                        <div key={i} className={`prog-item mobile-item w-100 d-flex align-items-center justify-content-between p-3 border-0 bg-transparent transition-all ${selectedProg.name === name ? 'is-active' : ''}`}
                          onClick={() => handleSelect(name, group.financialYear, "Graduate")}>
                            <div className="d-flex align-items-center gap-3">
                                <div className={`icon-circle-mini ${selectedProg.name === name ? 'active' : ''}`}>
                                    <FaGraduationCap />
                                </div>
                                <div className="text-start">
                                    <div className="name fw-bold mb-0">{name}</div>
                                    <div className="stats-tag">{gi === 0 ? "Masters" : "M.Phil/PhD"}</div>
                                </div>
                            </div>
                            {selectedProg.name === name && <FaChevronRight className="chevron-mini visible" />}
                        </div>
                      ))}
                    </div>
                  ))}
                </Accordion.Body>
              </Accordion.Item>
            )}
          </Accordion>
        </Offcanvas.Body>
      </Offcanvas>

      <Footer />

      <style>{`
        .portal-master-wrapper { background-color: #fff; min-height: 100vh; font-family: 'Inter', sans-serif; overflow-x: hidden; }
        .bg-light-soft { background-color: #f8faff; }
        .fw-900 { font-weight: 900; }
        .text-navy { color: #002b5a; }
        .ls-1 { letter-spacing: 1px; }

        /* Typography Sync */
        .category-tag { color: #0055a4; font-weight: 800; text-transform: uppercase; font-size: 0.8rem; letter-spacing: 2px; }
        .portal-heading { font-weight: 900; letter-spacing: -1.5px; line-height: 1.1; }
        .ivy-divider { width: 60px; height: 5px; background: #0055a4; border-radius: 10px; }
        
        .sync-dot-mini {
          width: 8px; height: 8px; background: #10b981;
          border-radius: 50%; display: inline-block;
          animation: pulse-green 2s infinite;
        }
        @keyframes pulse-green {
          0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); }
          70% { transform: scale(1); box-shadow: 0 0 0 5px rgba(16, 185, 129, 0); }
          100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
        }

        .main-portal-container { margin-top: 0; padding-bottom: 100px; position: relative; z-index: 10; }
        
        .search-pill-container {
          background: #f1f4f9; border-radius: 50px; padding: 10px 25px;
          display: flex; align-items: center; transition: 0.3s;
        }
        .search-pill-container:focus-within { background: #fff; box-shadow: 0 10px 25px rgba(0,0,0,0.05); border: 1px solid #0055a4; }
        .search-pill-container input { border: none; background: transparent; outline: none; width: 100%; margin-left: 12px; font-weight: 500; color: #002b5a; }
        .search-icon { color: #94a3b8; }

        /* Sidebar Styling */
        .sticky-sidebar { position: sticky; top: 120px; z-index: 100; transition: top 0.3s; }
        .sidebar-card { background: #fff; border-radius: 24px; overflow: hidden; display: flex; flex-direction: column; max-height: 75vh; border: 1px solid #e2e8f0 !important; }
        
        .card-label { 
          padding: 18px 25px; background: #002b5a; color: white; 
          font-weight: 800; font-size: 0.75rem; letter-spacing: 2px; text-transform: uppercase;
        }

        .program-list { overflow-y: auto; flex: 1; }
        .custom-scrollbar::-webkit-scrollbar { width: 5px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }

        /* Accordion Overrides */
        .premium-accordion .accordion-item { border: none; background: transparent; }
        .premium-accordion .accordion-button { 
           padding: 15px 25px; font-weight: 800; color: #475569; background: #fff; box-shadow: none; border-bottom: 1px solid #f1f5f9;
           text-transform: uppercase; font-size: 0.85rem; letter-spacing: 1px;
        }
        .premium-accordion .accordion-button:not(.collapsed) { background: #f8fafc; color: #002b5a; }
        .premium-accordion .accordion-button:focus { box-shadow: none; }
        .premium-accordion .accordion-button::after { transform: scale(0.8); }

        .prog-item {
          transition: 0.3s;
          border-bottom: 1px solid #f1f5f9 !important;
          cursor: pointer;
        }
        .prog-item:hover { background-color: #f8fafc !important; }
        .prog-item.is-active {
          background-color: #002b5a !important;
          border-left: 6px solid #ffc300 !important;
        }
        .prog-item.is-active .name { color: white !important; }
        .prog-item.is-active .stats-tag { color: rgba(255,255,255,0.7) !important; }
        .prog-item.is-active .icon-circle-mini { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.2); color: white; }

        .icon-circle-mini {
          width: 38px; height: 38px; background: #f8fafc; border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          font-size: 1rem; color: #64748b; transition: 0.3s;
          border: 1px solid #e2e8f0;
        }
        .icon-circle-mini.active { background: #002b5a; color: #fff; border-color: #002b5a; }
        .custom-scrollbar::-webkit-scrollbar { width: 5px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }

        .stats-tag { font-size: 0.75rem; color: #94a3b8; font-weight: 600; }
        .chevron-mini { font-size: 0.8rem; color: #cbd5e0; opacity: 0; transition: 0.3s; transform: translateX(-10px); }
        .chevron-mini.visible { opacity: 1; transform: translateX(0); color: #0055a4; }
        .prog-item.is-active .chevron-mini.visible { color: #ffc300; }

        .details-card { border-radius: 24px !important; border: 1px solid #e2e8f0 !important; transition: 0.3s; }
        .border-dashed { border: 2px dashed #e2e8f0 !important; }

        .premium-fee-table thead th { border-top: none; color: #64748b; font-weight: 800; background: #fdfdfd; padding: 15px; border-bottom: 2px solid #f1f5f9; }
        .premium-fee-table tbody td { vertical-align: middle; border-bottom: 1px solid #f1f5f9; }
        
        .italic { font-style: italic; }
        .fw-500 { font-weight: 500; }

        @media (max-width: 991px) {
          .main-portal-container { margin-top: 0px; }
          .section-standard-spacing { padding-bottom: 30px; }
          .portal-heading { font-size: 1.6rem; word-break: break-word; letter-spacing: -1px; }
          .category-tag { font-size: 0.7rem; letter-spacing: 1.5px; }
          .details-card { padding: 1rem !important; }
          
          /* Mobile Table Density */
          .premium-fee-table { display: block; width: 100%; }
          .premium-fee-table tbody, .premium-fee-table tr, .premium-fee-table td { display: block; width: 100%; }
          .fee-row { padding: 10px 0; border-bottom: 1px solid #f8faff; }
          .fee-row:last-child { border-bottom: none; }
          .fee-label { font-size: 0.65rem; color: #94a3b8 !important; padding: 0 !important; }
          .fee-value { font-size: 0.9rem; text-align: left !important; padding: 0 !important; border: none !important; word-break: break-word; margin-top: 2px; }
          .remarks-cell { font-size: 0.8rem !important; margin-top: 4px; line-height: 1.4; }
          
          .program-list-mobile .prog-item.mobile-item { border-bottom: 1px solid #f1f5f9 !important; padding: 18px; }
          .program-list-mobile .prog-item.mobile-item.is-active { 
            background: #002b5a !important; 
            color: white !important; 
            border-left: 5px solid #ffc300 !important; 
          }
          .program-list-mobile .prog-item.mobile-item.is-active .name { color: white !important; }
          .program-list-mobile .prog-item.mobile-item.is-active .stats-tag { color: rgba(255,255,255,0.7) !important; }
          .program-list-mobile .prog-item.mobile-item.is-active .icon-circle-mini { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.2); color: white; }
          .sticky-sidebar { position: relative !important; top: 0 !important; }
        }

        /* Navigation Hint Pulse */
        @keyframes hintPulse {
          0% { box-shadow: 0 0 0 0 rgba(0, 85, 164, 0.4); }
          70% { box-shadow: 0 0 0 10px rgba(0, 85, 164, 0); }
          100% { box-shadow: 0 0 0 0 rgba(0, 85, 164, 0); }
        }
        .nav-hint-pulse {
          animation: hintPulse 2s infinite;
          border: 2px solid #fff !important;
        }
      `}</style>
    </div>
  );
};

export default FeeStructure;
